import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scrollableTableSizing } from '../styles/scrollableTable';
import { FiTag, FiPlus, FiEdit, FiTrash2, FiSearch, FiRefreshCw, FiX, FiImage } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow-x: visible;
  min-width: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.variant === 'primary' ? '#5efba9' : 'white'};
  color: ${props => props.variant === 'primary' ? '#1f2937' : '#6b7280'};
  border: ${props => props.variant === 'secondary' ? '1px solid #e5e7eb' : 'none'};

  &:hover {
    background: ${props => props.variant === 'primary' ? '#4de89e' : '#f9fafb'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 32px;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 24px;
  transition: all 0.2s;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 10px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #6b7280;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #5efba9;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4de89e;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 4px;
    
    &:hover {
      background: #9ca3af;
    }
  }
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f9fafb;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #1f2937;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 12px;
    padding: 10px 12px;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.active ? '#166534' : '#991b1b'};
`;

const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
`;

const UploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #f9fafb;

  &:hover {
    border-color: #5efba9;
    background: #f0fdf4;
  }

  input {
    display: none;
  }
`;

const ImagePreview = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const BrandImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export default function SuperAdminBrands() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandEmails, setBrandEmails] = useState({}); // Map of brandId -> email from auth
  const [brandProductCounts, setBrandProductCounts] = useState({}); // Map of brandId -> total products
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    income: '',
    img: '',
    insta: '',
    active: true,
    phone: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.createRef();

  useEffect(() => {
    if (session) {
      fetchBrands();
    } else {
      console.log('⏳ Waiting for session to load...');
      setLoading(false);
    }
  }, [session]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      console.log('📋 Fetching brands from Supabase...');
      
      // Check if session exists
      if (!session) {
        console.warn('⚠️ No active session - cannot fetch brands');
        setBrands([]);
        setLoading(false);
        return;
      }

      // Check Supabase connection
      const { data: testData, error: testError } = await supabase
        .from('brands')
        .select('id')
        .limit(1);

      if (testError) {
        console.error('❌ Supabase connection error:', testError);
        throw new Error(`Database connection failed: ${testError.message}`);
      }

      console.log('✅ Supabase connection verified');

      const { data, error } = await supabase
        .from('brands')
        .select(`
          *,
          creator:profiles!brands_created_by_fkey(
            id,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching brands:', error);
        throw new Error(`Failed to fetch brands: ${error.message || 'Unknown error'}`);
      }

      console.log(`✅ Fetched ${data?.length || 0} brands`);
      setBrands(data || []);

      // Fetch product counts per brand
      const countsMap = {};
      const brandIds = (data || []).map(brand => brand.id).filter(Boolean);
      if (brandIds.length > 0) {
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select('brand_id')
          .in('brand_id', brandIds);

        if (productsError) {
          console.error('❌ Error fetching product counts for brands:', productsError);
        } else if (productsData) {
          productsData.forEach((product) => {
            const brandId = product.brand_id;
            if (!brandId) return;
            countsMap[brandId] = (countsMap[brandId] || 0) + 1;
          });
        }
      }
      setBrandProductCounts(countsMap);
      
      // Fetch emails from auth for each brand owner (non-blocking, optional)
      const emailsMap = {};
      if (data && data.length > 0) {
        console.log('📧 Fetching emails from auth for brand owners...');
        
        // Fetch emails in parallel with timeout to prevent blocking
        const emailPromises = data.map(async (brand) => {
          if (!brand.created_by) return null;
          
          try {
            const { data: email, error: rpcError } = await supabase.rpc('get_user_email', { 
              user_id: brand.created_by 
            });
            
            if (rpcError) {
              console.error(`❌ RPC error fetching email for brand ${brand.id} (profile ${brand.created_by}):`, rpcError.message);
              return null;
            } else if (email) {
              console.log(`✅ Email found for brand "${brand.name}": ${email}`);
              return { brandId: brand.id, email };
            } else {
              console.warn(`⚠️ No email found for brand "${brand.name}" (profile ${brand.created_by})`);
              return null;
            }
          } catch (emailError) {
            console.error(`❌ Exception fetching email for brand ${brand.id}:`, emailError?.message || emailError);
            return null;
          }
        });

        // Wait for all email fetches to complete (or timeout)
        const emailResults = await Promise.allSettled(emailPromises);
        
        emailResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            emailsMap[result.value.brandId] = result.value.email;
          }
        });

        setBrandEmails(emailsMap);
        console.log(`📧 Fetched ${Object.keys(emailsMap).length} emails for brands`);
      }
      
      // Debug: Log brand images
      if (data && data.length > 0) {
        console.log('Brands with images:', data.map(b => ({
          name: b.name,
          img: b.img,
          hasImg: !!b.img && b.img.trim() !== ''
        })));
      }
    } catch (err) {
      console.error('❌ Error fetching brands:', err);
      
      // Provide more helpful error messages
      let errorMessage = 'Failed to fetch brands';
      if (err && err.message) {
        errorMessage = err.message;
      } else if (err instanceof TypeError && err.message && err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
      } else {
        errorMessage = `Failed to fetch brands: ${err ? err.toString() : 'Unknown error'}`;
      }
      
      alert(errorMessage);
      setBrands([]);
      setBrandEmails({});
      setBrandProductCounts({});
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name: name,
      slug: generateSlug(name)
    }));
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);

    try {
      // Generate unique filename - ensure we have a valid extension
      let fileExt = file.name.split('.').pop();
      if (!fileExt || fileExt.length === 0) {
        // Fallback based on file type
        if (file.type.includes('jpeg') || file.type.includes('jpg')) fileExt = 'jpg';
        else if (file.type.includes('png')) fileExt = 'png';
        else if (file.type.includes('gif')) fileExt = 'gif';
        else if (file.type.includes('webp')) fileExt = 'webp';
        else fileExt = 'jpg'; // Default fallback
      }

      const randomSuffix = Math.random().toString(36).substring(2, 9); // Ensure we get enough characters
      const fileName = `brand_${Date.now()}_${randomSuffix}.${fileExt}`;

      console.log('=== UPLOAD DETAILS ===');
      console.log('Original file name:', file.name);
      console.log('File extension:', fileExt);
      console.log('Generated filename:', fileName);
      console.log('Full file name length:', fileName.length);
      console.log('File size:', file.size, 'bytes');

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('brands')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        alert('Failed to upload image: ' + uploadError.message);
        return;
      }

      console.log('Upload response:', uploadData);

      // Use the actual path from upload response
      const filePath = uploadData?.path || fileName;
      console.log('File path to use:', filePath);
      console.log('File path length:', filePath.length);

      // For private buckets, use signed URL (valid for 1 year = 31536000 seconds)
      // This creates a long-lived signed URL that works for private buckets
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('brands')
        .createSignedUrl(filePath, 31536000); // 1 year expiration

      if (signedUrlError) {
        console.error('Error creating signed URL:', signedUrlError);
        // Fallback: try public URL (in case bucket becomes public later)
        const { data: urlData } = supabase.storage
          .from('brands')
          .getPublicUrl(filePath);

        if (urlData?.publicUrl) {
          console.warn('Using public URL as fallback (bucket may be public)');
          const publicUrl = urlData.publicUrl;
          setFormData(prev => ({ ...prev, img: publicUrl.trim() }));
          if (fileInputRef.current) fileInputRef.current.value = '';
          return;
        } else {
          alert('Failed to generate URL. Make sure the "brands" bucket exists and has proper permissions.');
          return;
        }
      }

      const signedUrl = signedUrlData?.signedUrl;
      console.log('=== URL GENERATION ===');
      console.log('Signed URL data:', signedUrlData);
      console.log('Signed URL:', signedUrl);
      console.log('Signed URL length:', signedUrl ? signedUrl.length : 0);
      console.log('Expected filename in URL:', fileName);

      if (!signedUrl) {
        alert('Failed to generate signed URL');
        return;
      }

      // Validate URL is complete - check if filename appears in URL
      const urlFileName = signedUrl.split('/').pop()?.split('?')[0]; // Remove query params
      console.log('Filename extracted from URL:', urlFileName);
      console.log('Expected filename:', fileName);

      if (urlFileName !== fileName && !signedUrl.includes(fileName)) {
        console.warn('WARNING: URL filename does not match!');
        console.warn('URL filename:', urlFileName);
        console.warn('Expected filename:', fileName);
      }

      // Use signed URL for private bucket
      const imageUrl = signedUrl;

      // Automatically set the img field with the signed URL
      setFormData(prev => ({
        ...prev,
        img: imageUrl.trim() // Ensure no extra whitespace
      }));

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      console.error('Error uploading file:', err);
      alert('Failed to upload image: ' + err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name || '',
      slug: brand.slug || '',
      description: brand.description || '',
      income: brand.income || '',
      img: brand.img || '',
      insta: brand.insta || '',
      active: brand.active !== undefined ? brand.active : true,
      phone: brand.phone || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    const brandName = brand?.name || 'this brand';

    try {
      // Get counts of all related data
      const [productsResult, orderItemsResult, voucherBrandsResult, brandManagersResult] = await Promise.all([
        supabase.from('products').select('id', { count: 'exact', head: true }).eq('brand_id', brandId),
        supabase.from('order_items').select('id', { count: 'exact', head: true }).eq('brand_id', brandId),
        supabase.from('voucher_brands').select('id', { count: 'exact', head: true }).eq('brand_id', brandId),
        supabase.from('brand_managers').select('id', { count: 'exact', head: true }).eq('brand_id', brandId)
      ]);

      const productsCount = productsResult.count || 0;
      const orderItemsCount = orderItemsResult.count || 0;
      const voucherBrandsCount = voucherBrandsResult.count || 0;
      const brandManagersCount = brandManagersResult.count || 0;

      // Get order IDs that will be affected (orders where all items are from this brand)
      let affectedOrderIds = [];
      if (orderItemsCount > 0) {
        // Get all order_items for this brand with their order_id
        const { data: orderItemsData } = await supabase
          .from('order_items')
          .select('order_id, id')
          .eq('brand_id', brandId);

        if (orderItemsData && orderItemsData.length > 0) {
          const orderIds = [...new Set(orderItemsData.map(item => item.order_id))];
          
          // For each order, check if ALL its items belong to this brand
          for (const orderIdToCheck of orderIds) {
            const { data: allOrderItems } = await supabase
              .from('order_items')
              .select('brand_id')
              .eq('order_id', orderIdToCheck);
            
            // If all items in this order belong to this brand, the order will be deleted
            const allItemsFromThisBrand = allOrderItems?.every(item => item.brand_id === brandId);
            if (allItemsFromThisBrand) {
              affectedOrderIds.push(orderIdToCheck);
            }
          }
        }
      }

      const ordersCount = affectedOrderIds.length;

      // Show detailed confirmation with counts
      const confirmationMessage = `
Are you sure you want to delete "${brandName}"?

This will PERMANENTLY delete:
• ${productsCount} product(s)
• ${orderItemsCount} order item(s)
• ${ordersCount} order(s) (where all items belong to this brand)
• ${voucherBrandsCount} voucher association(s)
• ${brandManagersCount} brand manager relationship(s)
• The brand itself

⚠️ WARNING: This action CANNOT be undone!

Do you want to continue?`;

      if (!window.confirm(confirmationMessage)) {
        return;
      }

      console.log(`🗑️ Calling Edge Function to delete brand "${brandName}" (ID: ${brandId})...`);

      // Call Edge Function to delete brand (bypasses RLS using service role)
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://lxprwdplyftigaemzaua.supabase.co';
      const functionUrl = `${supabaseUrl}/functions/v1/delete-brand`;
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        throw new Error('No active session. Please log in again.');
      }

      let response;
      let result;
      
      try {
        response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY || '',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ brandId }),
        });

        // Check if response is OK before trying to parse JSON
        if (!response.ok) {
          // If 404, the function doesn't exist - needs to be deployed
          if (response.status === 404) {
            throw new Error(`Edge Function "delete-brand" not found. Please deploy it first:\n\nRun: supabase functions deploy delete-brand\n\nOr deploy via Supabase Dashboard → Edge Functions`);
          }
          
          // Try to get error message from response
          try {
            result = await response.json();
            throw new Error(result.error || `Failed to delete brand: ${response.status} ${response.statusText}`);
          } catch (parseError) {
            throw new Error(`Failed to delete brand: ${response.status} ${response.statusText}`);
          }
        }

        result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to delete brand: Unknown error');
        }
      } catch (fetchError) {
        console.error('❌ Error calling Edge Function:', fetchError);
        
        // Check if it's a network error
        if (fetchError instanceof TypeError && fetchError.message.includes('Failed to fetch')) {
          throw new Error(`Cannot connect to Edge Function. Possible causes:\n\n1. Edge Function not deployed yet\n   → Run: supabase functions deploy delete-brand\n\n2. Network/CORS issue\n   → Check your internet connection\n   → Verify Supabase URL is correct\n\n3. Function URL incorrect\n   → Current URL: ${functionUrl}\n\nSee console for details.`);
        }
        
        // Re-throw the error with its message
        throw fetchError;
      }

      console.log(`✅ Brand "${brandName}" and all related data deleted successfully`);
      console.log(`📊 Deletion summary:`, result.deleted);
      
      alert(`Brand "${brandName}" and all related data deleted successfully!\n\nDeleted:\n• ${result.deleted.products} product(s)\n• ${result.deleted.orderItems} order item(s)\n• ${result.deleted.orders} order(s)\n• ${result.deleted.voucherBrands} voucher association(s)\n• ${result.deleted.brandManagers} brand manager relationship(s)`);
      setBrands(brands.filter(b => b.id !== brandId));
    } catch (err) {
      console.error('Error deleting brand:', err);
      alert('Failed to delete brand: ' + err.message + '\n\nPlease check the console for details.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let brandData;
      let error;

      if (editingBrand) {
        // When editing, don't update slug or website (slug can't be updated per DB constraint)
        // Use existing slug for website generation
        const existingSlug = editingBrand.slug || formData.slug.trim() || generateSlug(formData.name);
        const website = `${window.location.origin}/${existingSlug}`;

        brandData = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          income: formData.income.trim() || null,
          img: formData.img.trim() || null,
          insta: formData.insta.trim() || null,
          website: website,
          active: formData.active,
          is_active: formData.active,
          phone: formData.phone.trim() || null,
        };

        const { error: updateError } = await supabase
          .from('brands')
          .update(brandData)
          .eq('id', editingBrand.id);
        error = updateError;

        // If brand active status changed, update the creator's is_admin status
        if (!error && editingBrand.created_by && editingBrand.active !== formData.active) {
          console.log('Brand active status changed:', {
            from: editingBrand.active,
            to: formData.active,
            creator: editingBrand.created_by
          });

          // Update profile is_admin based on brand active status
          const { error: profileError } = await supabase
            .from('profiles')
            .update({ is_admin: formData.active })
            .eq('id', editingBrand.created_by);

          if (profileError) {
            console.error('Error updating profile admin status:', profileError);
            // Don't throw - brand was updated successfully, just log the profile update error
          } else {
            console.log('Profile is_admin updated to:', formData.active);
          }

          // Also manage brand_managers table
          if (formData.active) {
            // Brand activated - ensure brand_managers entry exists
            const { error: managerError } = await supabase
              .from('brand_managers')
              .upsert({
                profile_id: editingBrand.created_by,
                brand_id: editingBrand.id
              }, {
                onConflict: 'profile_id,brand_id'
              });

            if (managerError) {
              console.error('Error creating brand_managers entry:', managerError);
            } else {
              console.log('Brand manager entry created/updated');
            }
          } else {
            // Brand deactivated - remove brand_managers entry
            const { error: deleteManagerError } = await supabase
              .from('brand_managers')
              .delete()
              .eq('profile_id', editingBrand.created_by)
              .eq('brand_id', editingBrand.id);

            if (deleteManagerError) {
              console.error('Error deleting brand_managers entry:', deleteManagerError);
            } else {
              console.log('Brand manager entry deleted');
            }
          }
        }
      } else {
        // When creating new brand, don't include slug - let database generate it
        // First insert without slug to let DB generate it
        
        // Ensure user has a profile entry before creating brand (if created_by is set)
        const userId = session?.user?.id || formData.created_by;
        if (userId) {
          const { data: existingProfile, error: profileCheckError } = await supabase
            .from('profiles')
            .select('id')
            .eq('id', userId)
            .maybeSingle();

          if (profileCheckError) {
            console.error('Error checking profile:', profileCheckError);
          }

          // If profile doesn't exist, create it
          if (!existingProfile) {
            const { error: profileCreateError } = await supabase
              .from('profiles')
              .upsert({
                id: userId,
                full_name: null,
              }, {
                onConflict: 'id'
              });

            if (profileCreateError) {
              console.error('Error creating profile:', profileCreateError);
              throw new Error('Failed to create user profile. Please ensure the user exists in the profiles table.');
            }
          }
        }

        brandData = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          income: formData.income.trim() || null,
          img: formData.img.trim() || null,
          insta: formData.insta.trim() || null,
          active: formData.active,
          is_active: formData.active,
          created_by: userId || null,
          phone: formData.phone.trim() || null,
        };

        // Check if user already has a brand (prevent multiple brands per user)
        if (userId) {
          const { data: existingUserBrands, error: checkError } = await supabase
            .from('brands')
            .select('id, name')
            .eq('created_by', userId);

          if (checkError) {
            console.error('Error checking existing brands:', checkError);
          }

          if (existingUserBrands && existingUserBrands.length > 0) {
            throw new Error(`This user already has a brand (${existingUserBrands[0].name}). Only one brand per user is allowed.`);
          }
        }

        const { data: insertedBrand, error: insertError } = await supabase
          .from('brands')
          .insert([brandData])
          .select()
          .single();

        if (insertError) {
          error = insertError;
        } else {
          // Now update with website using the generated slug
          const slug = insertedBrand.slug || generateSlug(formData.name);
          const website = `${window.location.origin}/${slug}`;

          const { error: updateError } = await supabase
            .from('brands')
            .update({ website: website })
            .eq('id', insertedBrand.id);

          error = updateError;

          // If new brand is created as active and has a creator, set them as admin
          if (!error && insertedBrand.created_by && formData.active) {
            console.log('New brand created as active, setting creator as admin:', insertedBrand.created_by);

            // Update profile is_admin to true
            const { error: profileError } = await supabase
              .from('profiles')
              .update({ is_admin: true })
              .eq('id', insertedBrand.created_by);

            if (profileError) {
              console.error('Error updating profile admin status:', profileError);
            } else {
              console.log('Profile is_admin set to true');
            }

            // Create brand_managers entry
            const { error: managerError } = await supabase
              .from('brand_managers')
              .upsert({
                profile_id: insertedBrand.created_by,
                brand_id: insertedBrand.id
              }, {
                onConflict: 'profile_id,brand_id'
              });

            if (managerError) {
              console.error('Error creating brand_managers entry:', managerError);
            } else {
              console.log('Brand manager entry created');
            }
          }
        }
      }

      if (error) throw error;

      alert(editingBrand ? 'Brand updated successfully!' : 'Brand created successfully!');
      setIsModalOpen(false);
      setEditingBrand(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        income: '',
        img: '',
        insta: '',
        active: true,
        phone: '',
      });
      fetchBrands();
    } catch (err) {
      console.error('Error saving brand:', err);
      alert('Failed to save brand: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBrand(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      income: '',
      img: '',
      insta: '',
      active: true,
      phone: '',
    });
  };

  const filteredBrands = brands.filter(brand => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      brand.name?.toLowerCase().includes(searchLower) ||
      brand.slug?.toLowerCase().includes(searchLower) ||
      brand.description?.toLowerCase().includes(searchLower) ||
      brand.phone?.toLowerCase().includes(searchLower) ||
      (brandEmails[brand.id]?.toLowerCase().includes(searchLower)) ||
      brand.income?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <>
      <Container>
        <Header>
          <Title>
            <FiTag /> {isArabic ? 'إدارة العلامات التجارية' : 'Brands Management'}
          </Title>
          <HeaderActions>
            <SearchBar>
              <FiSearch />
              <SearchInput
                type="text"
                placeholder={isArabic ? 'ابحث في العلامات...' : 'Search brands...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            <Button variant="secondary" onClick={fetchBrands}>
              <FiRefreshCw /> {isArabic ? 'تحديث' : 'Refresh'}
            </Button>
          </HeaderActions>
        </Header>

        {loading ? (
          <EmptyState>{isArabic ? 'جارٍ تحميل العلامات...' : 'Loading brands...'}</EmptyState>
        ) : filteredBrands.length === 0 ? (
          <TableWrapper>
            <EmptyState>
              <EmptyIcon>🏷️</EmptyIcon>
              <EmptyTitle>{isArabic ? 'لا توجد علامات بعد' : 'No Brands Yet'}</EmptyTitle>
              <EmptyText>
                {searchTerm
                  ? (isArabic ? 'لا توجد علامات مطابقة لبحثك' : 'No brands match your search criteria')
                  : (isArabic ? 'ابدأ بإضافة أول علامة تجارية' : 'Get started by adding your first brand')}
              </EmptyText>
            </EmptyState>
          </TableWrapper>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>{isArabic ? 'الصورة' : 'Image'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الاسم' : 'Name'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'المنتجات' : 'Products'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'المالك' : 'Owner'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'البريد الإلكتروني' : 'Email'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الهاتف' : 'Phone'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الدخل المتوقع' : 'Expected Income'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الوصف' : 'Description'}</TableHeaderCell>
                  <TableHeaderCell>Instagram</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الحالة' : 'Status'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</TableHeaderCell>
                  <TableHeaderCell>{isArabic ? 'الإجراءات' : 'Actions'}</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {filteredBrands.map((brand) => (
                  <TableRow key={brand.id}>
                    <TableCell>
                      {brand.img && brand.img.trim() !== '' ? (
                        <BrandImage
                          src={brand.img}
                          alt={brand.name || 'Brand'}
                          onError={(e) => {
                            console.error('Brand image failed to load:', brand.img, 'for brand:', brand.name);
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.style.cssText = 'width: 50px; height: 50px; background: #f3f4f6; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 20px;';
                            fallback.textContent = '🏷️';
                            e.target.parentElement.appendChild(fallback);
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: '#f3f4f6',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          fontSize: '20px'
                        }}>
                          🏷️
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <strong>{brand.name}</strong>
                    </TableCell>
                    <TableCell>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '34px',
                        height: '24px',
                        padding: '0 8px',
                        borderRadius: '999px',
                        background: '#ecfdf3',
                        color: '#166534',
                        border: '1px solid #bbf7d0',
                        fontWeight: 700,
                        fontSize: '12px'
                      }}>
                        {brandProductCounts[brand.id] || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {brand.creator ? (
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>
                          {brand.creator.full_name || (isArabic ? 'بدون اسم' : 'No name')}
                        </div>
                      ) : brand.created_by ? (
                        <span style={{ color: '#6b7280' }}>{isArabic ? 'مستخدم غير معروف' : 'Unknown User'}</span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>{isArabic ? 'لا يوجد مالك' : 'No owner'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {brandEmails[brand.id] ? (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          color: '#5efba9',
                          fontWeight: '500'
                        }}>
                          <span style={{ fontSize: '16px' }}>📧</span>
                          <span>{brandEmails[brand.id]}</span>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{isArabic ? 'لا يوجد بريد' : 'No email'}</span>
                      )}
                    </TableCell>
                    <TableCell>{brand.phone || '-'}</TableCell>
                    <TableCell>{brand.income || '-'}</TableCell>
                    <TableCell>
                      {brand.description ? (
                        <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {brand.description}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {brand.insta ? (() => {
                        // Normalize Instagram URL
                        let instaUrl = brand.insta.trim();
                        let displayText = instaUrl;
                        
                        // If it's already a full URL, use it as is
                        if (instaUrl.startsWith('http://') || instaUrl.startsWith('https://')) {
                          displayText = instaUrl.replace(/https?:\/\/(www\.)?instagram\.com\//, '');
                          if (!displayText) {
                            displayText = instaUrl;
                          }
                        } else {
                          // Remove @ and any instagram.com prefix, then construct URL
                          const username = instaUrl.replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//, '').trim();
                          instaUrl = `https://instagram.com/${username}`;
                          displayText = username;
                        }
                        
                        return (
                          <a 
                            href={instaUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            style={{ 
                              color: '#5efba9', 
                              textDecoration: 'none',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#34d399'}
                            onMouseLeave={(e) => e.target.style.color = '#5efba9'}
                          >
                            @{displayText}
                          </a>
                        );
                      })() : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={brand.active}>
                        {brand.active ? (isArabic ? 'نشط' : 'Active') : (isArabic ? 'غير نشط' : 'Inactive')}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      {brand.created_at
                        ? new Date(brand.created_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <ActionButtonsCell>
                        <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleEdit(brand)}>
                          <FiEdit />
                        </Button>
                        <Button variant="secondary" style={{ padding: '6px 12px', color: '#dc2626' }} onClick={() => handleDelete(brand.id)}>
                          <FiTrash2 />
                        </Button>
                      </ActionButtonsCell>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </Container>

      {isModalOpen && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingBrand ? (isArabic ? 'تعديل العلامة' : 'Edit Brand') : (isArabic ? 'إضافة علامة جديدة' : 'Add New Brand')}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>

            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>{isArabic ? 'اسم العلامة *' : 'Brand Name *'}</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                  placeholder={isArabic ? 'أدخل اسم العلامة' : 'Enter brand name'}
                />
              </FormGroup>

              {/* Add phone field */}
              <FormGroup>
                <Label>{isArabic ? 'رقم الهاتف *' : 'Phone Number *'}</Label>
                <Input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder={isArabic ? 'أدخل رقم الهاتف' : 'Enter phone number'}
                />
              </FormGroup>

              {/* Add email field */}
              <FormGroup>
                <Label>{isArabic ? 'المعرف (Slug) *' : 'Slug *'}</Label>
                <Input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  placeholder={isArabic ? 'brand-slug (تلقائي)' : 'brand-slug (auto-generated)'}
                  readOnly={!editingBrand}
                  style={!editingBrand ? {
                    backgroundColor: '#f9fafb',
                    cursor: 'not-allowed',
                    color: '#6b7280'
                  } : {}}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  {editingBrand
                    ? (isArabic ? 'معرف مناسب للرابط (قابل للتعديل)' : 'URL-friendly identifier (editable for existing brands)')
                    : (isArabic ? 'معرف مناسب للرابط (يُولد تلقائيًا)' : 'URL-friendly identifier (auto-generated from name)')}
                </small>
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'الوصف' : 'Description'}</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder={isArabic ? 'أدخل وصف العلامة' : 'Enter brand description'}
                />
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'المتوسط الشهري' : 'Monthly Average'}</Label>
                <Input
                  type="text"
                  name="income"
                  value={formData.income}
                  onChange={handleInputChange}
                  placeholder={isArabic ? 'مثال: 500 - 1000 ريال' : 'e.g., 500 - 1000 OMR'}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  {isArabic ? 'الدخل الشهري المتوقع من المبيعات (اختياري)' : 'Estimated monthly income from sales (optional)'}
                </small>
              </FormGroup>

              <FormGroup>
                <Label>{isArabic ? 'صورة العلامة' : 'Brand Image'}</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploadingImage}
                  style={{ display: 'none' }}
                />
                <UploadArea onClick={() => fileInputRef.current?.click()}>
                  {uploadingImage ? (
                    <div>
                      <p style={{ color: '#5efba9', margin: 0 }}>{isArabic ? 'جارٍ الرفع...' : 'Uploading...'}</p>
                    </div>
                  ) : (
                    <>
                      <FiImage style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '12px' }} />
                      <p style={{ color: '#6b7280', margin: 0 }}>
                        {isArabic ? 'اضغط لاختيار صورة' : 'Click to select an image file'}
                      </p>
                      <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
                        {isArabic ? 'الامتدادات: JPG, PNG, GIF, WebP' : 'Supports: JPG, PNG, GIF, WebP'}
                      </p>
                    </>
                  )}
                </UploadArea>
                {formData.img && (
                  <>
                    <ImagePreview src={formData.img} alt={isArabic ? 'معاينة العلامة' : 'Brand preview'} />
                    <Input
                      type="text"
                      name="img"
                      value={formData.img}
                      onChange={handleInputChange}
                      placeholder={isArabic ? 'رابط الصورة (يتم تعبئته تلقائيًا بعد الرفع)' : 'Image URL (auto-filled after upload)'}
                      style={{ marginTop: '8px', fontSize: '12px', fontFamily: 'monospace' }}
                      readOnly
                    />
                    <small style={{ color: '#6b7280', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                      {isArabic ? 'طول الرابط الكامل:' : 'Full URL:'} {formData.img.length} {isArabic ? 'حرف' : 'characters'}
                    </small>
                  </>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Instagram</Label>
                <Input
                  type="text"
                  name="insta"
                  value={formData.insta}
                  onChange={handleInputChange}
                  placeholder={isArabic ? '@username أو instagram.com/username' : '@username or instagram.com/username'}
                />
                <small style={{ color: '#6b7280', fontSize: '12px' }}>
                  {isArabic ? 'أدخل اسم مستخدم إنستغرام أو الرابط الكامل' : 'Enter Instagram username (e.g., @username) or full URL'}
                </small>
              </FormGroup>

              <FormGroup>
                <CheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  <span>{isArabic ? 'نشط (ظاهر للعملاء)' : 'Active (visible to customers)'}</span>
                </CheckboxLabel>
              </FormGroup>

              <ModalActions>
                <CancelButton type="button" onClick={handleCloseModal}>
                  {isArabic ? 'إلغاء' : 'Cancel'}
                </CancelButton>
                <SaveButton type="submit" disabled={submitting}>
                  {submitting
                    ? (isArabic ? 'جارٍ الحفظ...' : 'Saving...')
                    : editingBrand
                      ? (isArabic ? 'تحديث العلامة' : 'Update Brand')
                      : (isArabic ? 'إنشاء العلامة' : 'Create Brand')}
                </SaveButton>
              </ModalActions>
            </Form>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}
